import {
	faArrowTurnDownLeft,
	faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { Link } from '@tanstack/react-router';
import { ShipmentStatus } from '@uturn/api/v3';
import { Icon } from '@uturn/ui-kit';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

type Location = {
	name: string;
	city: string;
};

type Price = {
	quantity: number;
	currency: string;
};

export type ShipmentSearchResult = {
	invoiceReference: string;
	containerNumber: string;
	shipmentNumber: number;
	id: number;
	departmentId: number;
	shipmentStatus: ShipmentStatus;
	agreedPrice?: Price;
	agreedShipmentFee?: Price;
	agreedTotalPrice?: Price;
	targetPrice: Price;
	targetShipmentFee: Price;
	targetTotalPrice: Price;
	carrierName: string;
	locations: Location[];
	_highlightResult: any;
};

const TAG_REPLACEMENT = {
	highlightPreTag: '<strong>',
	highlightPostTag: '</strong>',
};

const TAG_PLACEHOLDER = {
	highlightPreTag: '__ais-highlight__',
	highlightPostTag: '__/ais-highlight__',
};

function getHighlightedPartsFromString(highlightedValue: string) {
	const { highlightPostTag, highlightPreTag } = TAG_REPLACEMENT;

	const splitByPreTag = highlightedValue.split(highlightPreTag);
	const firstValue = splitByPreTag.shift();
	const elements = !firstValue
		? []
		: [{ value: firstValue, isHighlighted: false }];

	splitByPreTag.forEach((split) => {
		const splitByPostTag = split.split(highlightPostTag);

		elements.push({
			value: splitByPostTag[0],
			isHighlighted: true,
		});

		if (splitByPostTag[1] !== '') {
			elements.push({
				value: splitByPostTag[1],
				isHighlighted: false,
			});
		}
	});

	return elements;
}

function Highlighted({ value }: { value: string }) {
	const highlightedValue = value
		.replaceAll(
			TAG_PLACEHOLDER.highlightPreTag,
			TAG_REPLACEMENT.highlightPreTag,
		)
		.replaceAll(
			TAG_PLACEHOLDER.highlightPostTag,
			TAG_REPLACEMENT.highlightPostTag,
		);

	const parts = getHighlightedPartsFromString(highlightedValue);

	return (
		<>
			{parts.map((part, partIndex) => {
				if (Array.isArray(part)) {
					return (
						<Fragment key={partIndex}>
							{part.map((subPart, subPartIndex) => (
								<span key={subPartIndex}>{subPart.value}</span>
							))}
						</Fragment>
					);
				}

				if (part.isHighlighted) {
					return (
						<span key={partIndex} className="font-semibold text-foreground">
							{part.value}
						</span>
					);
				}

				return (
					<>
						{part.value.startsWith(' ') ? (
							<>&nbsp;{part.value.trim()}</>
						) : (
							<span key={partIndex}>{part.value}</span>
						)}
					</>
				);
			})}
		</>
	);
}

function getHighlightedValues(obj: any) {
	const highlightedParts: any[] = [];
	Object.entries(obj).forEach(([key, value]) => {
		const keyValue = (value as { value?: string })?.value ?? '';

		if (typeof keyValue === 'string') {
			const hasMatchWithQuery = keyValue
				.toLowerCase()
				.includes('ais-highlight');

			if (hasMatchWithQuery) {
				highlightedParts.push({ key, value: keyValue });
			}
		}
	});

	return highlightedParts;
}

function getHighlightedParts({ result }: { result: ShipmentSearchResult }) {
	const highlightedParts: any[] = [];

	const highlightedValues = getHighlightedValues(result._highlightResult);
	highlightedParts.push(...highlightedValues);

	(result._highlightResult.locations as Location[]).forEach((location) => {
		const highlightedValuesLocation = getHighlightedValues(location);
		highlightedParts.push(...highlightedValuesLocation);
	});

	return highlightedParts;
}

export function ShipmentSearchHit({
	result,
	handleCloseSearch,
}: {
	result: ShipmentSearchResult;
	handleCloseSearch?: () => void;
}) {
	const { t } = useTranslation();
	const highlightedParts = getHighlightedParts({
		result,
	});
	const status = result.shipmentStatus?.toLowerCase() ?? 'unknown';

	return (
		<Link
			to="/shipments/$shipmentNumber"
			params={{ shipmentNumber: result.shipmentNumber }}
			onClick={handleCloseSearch}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					event.currentTarget.click();
				}
			}}
		>
			<div className="group relative flex cursor-pointer flex-col items-start gap-1 px-5 py-2 hover:bg-accent hover:text-accent-foreground">
				<div className="flex">
					{highlightedParts.length > 0 &&
						highlightedParts.map(({ key, value }, index) => {
							if (index !== 0) {
								return null;
							}
							return <Highlighted key={key} value={value} />;
						})}
				</div>
				<span className="flex items-center gap-2 text-sm font-light text-muted-foreground">
					{t(`general.shipments.search_status.${status}`, '{{status}}', {
						status,
					})}
					<Icon
						className="size-3 text-muted-foreground"
						icon={faChevronRight}
					/>
					#{result.shipmentNumber}
				</span>
				<div className="absolute right-5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100">
					<Icon
						className="size-3 text-muted-foreground"
						icon={faArrowTurnDownLeft}
					/>
				</div>
			</div>
		</Link>
	);
}
